// action-button.component.ts
import {Component, Input, Output, EventEmitter, OnInit, OnDestroy, DestroyRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {PageButtonComponent} from '../buttons/page-button.component';
import {BaseButtonComponent} from '../buttons/base-button.component';
import {BottomSheetService} from '../../services/bottom-sheet.service';
import {ThemeService} from '../../services/theme-service';
import {ActivatedRoute, Router} from '@angular/router';
import {AppStateService} from '../../services/app-state.service';
import {Subscription} from 'rxjs';
import {MenuUtils} from '../../utils/Utils';
import {PaymentSessionService} from '../../services/payment-session.service';
import {TranslateService} from '../../services/translate.service';
import {AppState} from '../../models/AppState';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthService} from '../../services/auth.service';
import {FSVoucher} from '../../models/FSVoucher';
import {VoucherService} from '../../services/voucher.service';
import {PrebookMenu} from '../../models/Order';

@Component({
  standalone: true,
  imports: [CommonModule, ActionButtonComponent, PageButtonComponent, BaseButtonComponent],
  selector: 'app-order-page-nav',
  template: `
    <!-- Fixed bottom navigation -->

    <div class="shadow-[0_-4px_16px_0px_rgba(0,0,0,0.05)] max-w-lg md:max-w-6xl mx-auto fixed bottom-0 inset-x-0 backdrop-blur-md bg-theme-surface-80"
         [ngClass]="{
       'p-4 pb-safe': shouldShowMenuButtons || itemCount || payableItems || activeVouchers,
     }">
      <div class="flex flex-col md:flex-row md:items-center gap-4">
        <div *ngIf="!venueOpen" class="md:pr-4">
          <div class="flex flex-row items-center justify-center gap-2">
            <div class="bg-theme-notification2 rounded-full w-6 h-6 flex justify-center items-center text-theme-contrast text-sm"><i class="fas fa-close"></i></div>
            <div class="text-xl text-theme-text font-bold">{{getString("Stängt för beställning")}}</div>
          </div>
          <div class="text-theme-text2 flex flex-row items-center justify-center mt-1">{{getString("Just nu kan du inte beställa något här.")}}</div>
        </div>
        <!-- Menu buttons -->
        <div *ngIf="shouldShowMenuButtons" class="flex">
          <!-- A prebooking's guest menus: one button with the menu shown, opening them all as menu cards -->
          @if (prebookMenus.length) {
            <div class="flex-1 min-w-0 md:w-80 md:flex-none">
              <app-base-button [baseClass]="'w-full bg-theme-surface px-3 py-2 rounded-xl text-theme-secondarybutton text-lg border-2 border-theme-secondarybutton'"
                               stateColorScheme="surface" (onClick)="openPrebookMenus()">
                <div class="flex items-center gap-3 w-full min-w-0">
                  <i class="fas fa-book-open text-xl"></i>
                  <span class="flex-1 min-w-0 truncate text-left font-bold">{{ prebookMenuName() }}</span>
                  <span class="text-base tabular-nums">{{ prebookMenuNumber() }}/{{ prebookMenus.length }}</span>
                  <i class="fas fa-chevron-up"></i>
                </div>
              </app-base-button>
            </div>
          } @else {
            <div class="flex-1 md:w-32 md:flex-none">
              <app-page-button [text]="getString('Mat')" icon="fa-utensils" (onClick)="selectPage('food')" [selected]="selectedPageId=='food'"></app-page-button>
            </div>
            <div class="w-4"></div>
            <div class="flex-1 md:w-32 md:flex-none">
              <app-page-button [text]="getString('Dryck')" icon="fa-glass-water" (onClick)="selectPage('drinks')" [selected]="selectedPageId=='drinks'"></app-page-button>
            </div>
          }
        </div>
        <!-- Action buttons -->
        <div class="flex gap-2 md:ml-auto" *ngIf="itemCount || (payableItems && !isKioskMode()) || activeVouchers">
          <div *ngIf="activeVouchers" [class]="itemCount?'w-20 md:w-auto':'w-full md:w-auto'">
            <app-action-button [text]="itemCount?undefined:translate.getString('Biljetter')" [itemCount]="0" (onClick)="navVouchers()" icon="fa-ticket" [chevron]="false"></app-action-button>
          </div>
          <div *ngIf="payableItems && !isStandardKioskMode()" [class]="itemCount?'w-20 md:w-auto':'w-full md:w-auto'">
            <app-action-button [text]="itemCount?undefined:translate.getString('Betala notan')" [itemCount]="0" (onClick)="navLivesplit()" icon="fa-wallet" [chevron]="false"></app-action-button>
          </div>
          <div class="flex-1 md:w-auto" *ngIf="itemCount">
            <app-action-button [text]="actionTitle" [itemCount]="itemCount" (onClick)="navCart()"></app-action-button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class OrderPageNavComponent implements OnInit, OnDestroy {
  @Input() selectedPageId: string = '';
  @Output() onClick = new EventEmitter<MouseEvent>();
  active = false;
  itemCount: number = 0;
  actionTitle: string = "";
  payableItems: boolean = false;
  // @ts-ignore
  activeVouchers?: FSVoucher[] = null;
  shouldShowMenuButtons: boolean = false;
  prebookMenus: PrebookMenu[] = [];
  private appState?: AppState;
  private voucherSub?: Subscription;
  venueOpen = true;

  constructor(private themeService: ThemeService, private router: Router, private route: ActivatedRoute,
              private appStateService: AppStateService, private paymentSessionService: PaymentSessionService,
              protected translate: TranslateService, private destroyRef: DestroyRef, private authService: AuthService,
              protected voucherService: VoucherService, private sheetService: BottomSheetService) {}

  ngOnInit(): void {
    this.appStateService.observeAppState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appState => {
      this.appState = appState;
      this.itemCount = MenuUtils.getItemCount(appState.order?.items);
      this.updateActionTitle(appState);
      this.shouldShowMenuButtons = MenuUtils.getPagesWithVisibleItems(appState).length > 1;
      this.prebookMenus = MenuUtils.prebookMenus(appState);
      this.setupVoucherSub(appState);
      this.venueOpen = MenuUtils.isDeliverTypeOpen(appState.order.user_cfg.dt, appState.cfg);
    });

    this.paymentSessionService.observePaymentSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(session => {
      this.payableItems = session?.payableItems ?? false;
    });

    this.translate.translationsReceivedSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateActionTitle(this.appState!);
    });
  }

  private setupVoucherSub(appState: AppState) {
    const showVouchersButton = appState.cfg?.voucher?.collins_voucher_mode === 'modern';
    if (showVouchersButton && this.voucherSub == null) {
      this.voucherSub = this.voucherService.observeFilteredVouchers()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(vouchers => {
          console.log("Active vouchers", vouchers);
          if (vouchers && vouchers.length > 0) {
            this.activeVouchers = vouchers;
          } else {
            this.activeVouchers = undefined;
          }
        });
    } else {
      this.activeVouchers = undefined;
    }
  }

  private updateActionTitle(appState: AppState) {
    const isOpenOrder = appState?.order?.user_cfg.payment?.open_order ?? false;
    if (appState?.order?.prebook?.can_edit && appState.order.prebook.role === 'participant') {
      // A participant of a shared prebooking doesn't send it: the booker does
      this.actionTitle = this.translate.getString("Min beställning");
      return;
    }
    if (appState?.order?.prebook?.can_edit) {
      this.actionTitle = isOpenOrder ? this.translate.getString("Konfirmera bokning") : this.translate.getString("Betala och boka");
      return;
    }
    this.actionTitle = !isOpenOrder ? this.translate.getString("Beställ och betala") : this.translate.getString("Beställ");
  }

  ngOnDestroy(): void {
    this.voucherSub?.unsubscribe();
    this.voucherSub = undefined;
  }

  navCart() {
    this.router.navigate([this.appStateService.venueId, "cart"]);
  }

  selectPage(page: string) {
    console.log("Navigating to page: " + page);
    this.router.navigate([this.appStateService.venueId,"menu",page]);
  }

  prebookMenuName(): string {
    return this.prebookMenus.find(menu => menu.id === this.selectedPageId)?.name ?? "";
  }

  prebookMenuNumber(): number {
    return this.prebookMenus.findIndex(menu => menu.id === this.selectedPageId) + 1;
  }

  openPrebookMenus() {
    this.sheetService.displaySheetClass("prebook-menus", this.getString("Menyer"), {selectedPageId: this.selectedPageId});
  }

  navLivesplit() {
    this.router.navigate([this.appStateService.venueId, "livesplit"]);
  }

  navVouchers() {
    this.router.navigate([this.appStateService.venueId, "vouchers"]);
  }

  getString(string: string = ""): string {
    return this.translate.getString(string);
  }

  isKioskMode(): boolean {
    return this.authService.isKioskMode();
  }

  getKioskState(): string {
    return this.authService.getKioskState();
  }

  isStandardKioskMode(): boolean {
    return this.authService.isKioskMode() && this.authService.getKioskState() === 'default';
  }
}
